// Per-device dismissal state for FamFeel quick guides.
//
// Two layers:
//   · `localStorage` → permanent dismiss ("Got it, don't show again")
//   · `sessionStorage` → session-only dismiss ("Skip for now")
//
// `isGuideDismissed` returns true if EITHER layer has the id set.
// Guides are always re-openable from Settings via `resetGuide(id)`.

const LS_KEY = "famfeel_guide_dismissed_v1";
const SS_KEY = "famfeel_guide_session_dismissed_v1";

function readMap(storage, key) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMap(storage, key, map) {
  try {
    storage.setItem(key, JSON.stringify(map));
  } catch {
    /* quota or privacy-mode — silent */
  }
}

export function isGuideDismissed(id) {
  if (!id) return false;
  if (readMap(localStorage, LS_KEY)[id]) return true;
  if (readMap(sessionStorage, SS_KEY)[id]) return true;
  return false;
}

export function dismissGuidePermanent(id) {
  if (!id) return;
  const m = readMap(localStorage, LS_KEY);
  m[id] = Date.now();
  writeMap(localStorage, LS_KEY, m);
}

export function dismissGuideSession(id) {
  if (!id) return;
  const m = readMap(sessionStorage, SS_KEY);
  m[id] = Date.now();
  writeMap(sessionStorage, SS_KEY, m);
}

export function resetGuide(id) {
  if (!id) return;
  const m1 = readMap(localStorage, LS_KEY);
  if (m1[id]) {
    delete m1[id];
    writeMap(localStorage, LS_KEY, m1);
  }
  const m2 = readMap(sessionStorage, SS_KEY);
  if (m2[id]) {
    delete m2[id];
    writeMap(sessionStorage, SS_KEY, m2);
  }
}

// ----- Guide content --------------------------------------------------------

export const WELCOME_GUIDE = {
  id: "welcome_v1",
  title: { en: "Welcome to FamFeel", da: "Velkommen til FamFeel" },
  // Mandatory = no "Skip for now" link in the footer. New families
  // have to step through the whole tour at least once so they
  // understand the PIN/password model before sharing the join code.
  // They can still close the browser tab; the modal will re-appear
  // on next open until they tap Done on the last step.
  mandatory: true,
  steps: [
    {
      eyebrow: { en: "A quiet app for loud families", da: "En rolig app til livlige familier" },
      title: { en: "Welcome! Let's do a quick tour.", da: "Velkommen! Lad os tage en hurtig rundtur." },
      body: {
        en: "Only share the PIN and the password with your family - No one else!",
        da: "Del kun PIN og adgangskode med din familie — ingen andre!",
      },
    },
    {
      eyebrow: { en: "Add your family", da: "Tilføj din familie" },
      title: { en: "Start with the people (and pets).", da: "Start med personerne (og kæledyrene)." },
      body: {
        en: "Open Settings in menu, click + Add a member. Add a name, an emoji or photo (plus), assign a color and add a birthday. Up to 4 members on Free, more on paid plans. Pets count as members too — dog, cat, fish, anything with a name.",
        da: "Åbn Indstillinger i menuen og tryk + Tilføj medlem. Skriv et navn, vælg en emoji eller et foto (Plus), giv en farve og tilføj en fødselsdag. Op til 4 medlemmer på Gratis, flere på de betalte planer. Kæledyr tæller også som medlemmer — hund, kat, fisk, alt med et navn.",
      },
    },
    {
      eyebrow: { en: "Choose your mode", da: "Vælg jeres mode" },
      title: { en: "Calendar · Mood · Pets — pick your vibe.", da: "Kalender · Humør · Kæledyr — vælg jeres stil." },
      body: {
        en: "Calendar: a grid of what's coming up, no check-in cards. Mood: everyone's vibe at a glance, tinted by how they feel. Pets: walks, feeds and vet exams fill the dashboard. Each member can also override the family setting on their own device — Settings → This device.",
      da: "Famfeel har 3 grundlæggende funktioner. 1. Kalender: Familiens store overblik. Humør: Familiens humør med ét klik. Kæledyr: Gåture, fodring og dyrlægebesøg. Vælg hvad du vil bruge Famfeel til under Indstillinger → Familie → Dashboard-mode.",
      },
    },
    {
      eyebrow: { en: "Add to Home Screen", da: "Læg på hjemmeskærmen" },
      title: { en: "FamFeel installs like an app.", da: "FamFeel installeres som en app." },
      body: {
        en: "Open the browser menu (⋮ or ⋯). Tap Install app or Add to Home screen. Confirm — the FamFeel Ff icon will appear on your home screen. Each device does this once. Tap the Ff icon to land straight on the family dashboard.",
        da: "Åbn browsermenuen (⋮ eller ⋯). Tryk Installér app eller Føj til hjemmeskærm. Bekræft — Ff-ikonet dukker op på din hjemmeskærm. Hver enhed gør det én gang. Tryk på Ff-ikonet for at gå direkte til familiedashboardet.",
      },
    },
    {
      eyebrow: { en: "Notifications & this device", da: "Notifikationer & denne enhed" },
      title: { en: "Turn on notifications", da: "Slå notifikationer til" },
      body: {
        en: "Open Settings → This device. Turn on notifications so event reminders ping at the right time.\n\nThen under \"Who's on this device?\" pick yourself — or pick \"Shared device\" for a shared tablet.",
        da: "Åbn Indstillinger → Denne enhed. Slå notifikationer til, så påmindelser om begivenheder kommer på det rigtige tidspunkt. Gøres én gang på alle enheder.\n\nUnder \"Hvem er på denne enhed?\" vælg dig selv — eller vælg \"Delt enhed\" til en fælles tablet.",
      },
    },
    {
      eyebrow: { en: "Time to get going!", da: "Så er vi i gang!" },
      title: { en: "Make FamFeel feel like home ❤️", da: "Føl jer hjemme ❤️" },
      body: {
        en: "That's the basics. Open the settings any time to find Discover more — a deeper guide with calendar tips, chores, kitchen display and Subscribe to your phone calendar. We're so happy that you are here.",
        da: "Det var det basale. Åbn Indstillinger når som helst for at finde \"Opdag mere\" — en dybere guide med kalendertips, pligter, køkken-display og hvordan I abonnerer i telefonens kalender. Vi er så glade for at I er her.",
      },
    },
  ],
};

// Optional follow-up walkthrough for users who finished the Welcome
// guide and want to go deeper. Keeps the first-time onboarding short
// (5 cards) without losing the power-user content. Surfaced via the
// "Show Discover guide" button in Settings → Quick guides, and via
// the closing-card mention above.
export const DISCOVER_GUIDE = {
  id: "discover_v1",
  title: "Discover FamFeel",
  steps: [
    {
      eyebrow: "The shared calendar",
      title: "Tap any day to add an event.",
      body:
        "On Calendar mode, tap “Add event” or any day on the month grid to add to the calendar. Group into categories with different colours, or change under Settings → Members - to add event as neutral with each member's own color on the right of the event. Tag who the event is for so the right people get the heads-up.",
    },
    {
      eyebrow: "Time format",
      title: "14:30 or 2:30 PM — your call.",
      body:
        "Settings → Family → Time format. Auto follows your device's locale, or pick 24-hour or 12-hour to lock it across every member's view. Useful when half the family thinks in 24-hour and half doesn't.",
    },
    {
      eyebrow: "Chores & Check lists",
      title: "Hand things out, tick things off.",
      body:
        "Open the menu and tap Chores or Lists. Chores assign a recurring task to a family member. Lists are for shopping, packing, or anything you want everyone ticking off — it's possible to connect a list to an event.",
    },
    {
      eyebrow: "Pet care log",
      title: "One tap stamps the date.",
      body:
        "On a pet's card, log a walk, feed or fresh water with one tap — the rest of the family sees it instantly. Open Care log inside the pet for vet wellness and tick/flea treatments — countdowns tell you when the next one is due. History → on the same card scrolls back through every log.",
    },
    {
      eyebrow: "Subscribe in your phone calendar",
      title: "FamFeel events alongside your other ones.",
      body:
        "Settings → Family → Subscribe in iOS / Android / Google Calendar. Copy the link, paste into your phone's calendar app and FamFeel events appear next to your work meetings. Updates flow back to FamFeel; new events you add in Apple/Google won't appear here unless you import them.",
    },
    {
      eyebrow: "Kitchen display",
      title: "Turn a shared iPad into a family display.",
      body:
        "Settings → This device → Kitchen display, or tap the TV icon in the menu. The screen lands in ambient mode — greeting, time, member tiles or upcoming events sliding along. Walk past the fridge and see what the day holds without tapping anything. Tap to exit.",
    },
    {
      eyebrow: "You're all set",
      title: "FamFeel grows with you.",
      body:
        "Open Settings → Quick guides any time to revisit a walkthrough. Have a request or hit a bug? There's a Feedback button at the bottom of the menu — we read every one.",
    },
  ],
};

export const PETS_GUIDE = {
  id: "pets_v1",
  title: "Welcome to Pets-only mode",
  steps: [
    {
      eyebrow: "Pets-only · the calm pet log",
      title: "Welcome! Let's set up your pet brain.",
      body:
        "Pets only brings the pet log to the front — walks, feeds, fresh water, vet exams, tick & flea. Everyone in the family helps; nobody has to remember alone.",
    },
    {
      eyebrow: "Add your pets",
      title: "Each pet is a family member.",
      body:
        "Settings → Manage members → Add. Pick species (dog, cat, hamster, fish…) and tap an emoji or upload a photo (Plus). Pets count toward your member total, but Famfeel will love them either way.",
    },
    {
      eyebrow: "Care log · vet & tick/flea",
      title: "Set the next vet date once. The app remembers.",
      body:
        "Open the pet card → Care log → set vet date and tick/flea cycle. A small countdown sits on every device. Vet and flea reminders are Plus and up.",
    },
    {
      eyebrow: "Food and water reminders",
      title: "Fod and water reminders.",
      body:
        "Free families still get the full pet log and walk planner for 1 dog or cat; Plus adds a second. Optional food and fresh water reminders live on Plus, Grand and Extended. Unlimited dogs and cats live on Grand and Extended.",
    },
    {
      eyebrow: "Walks",
      title: "Walk planning.",
      body:
        "Tap the pet to open the log. Dogs get a walk planner with attendees and a 30-minute reminder. It's possible to plan the next 2 walks, assign a member and he/she will get a reminder 30 min before.",
    },
    {
      eyebrow: "All set",
      title: "Re-open this guide from Settings any time.",
      body:
        "Settings → \"Show pets guide\" brings it back. The pet pack almost runs itself from here 🐶🐱",
    },
  ],
};

export const HOUSES_GUIDE = {
  id: "houses_v1",
  title: "Welcome to Houses",
  steps: [
    {
      eyebrow: "Grand & Extended · Houses",
      title: "For blended & multi-household families.",
      body:
        "A Kid can live in both Mum's house and Dad's house. Grandparents can have their own. Each House has its own members, events, check-ins — and optionally its own front-door PIN so only the right people see inside.",
    },
    {
      eyebrow: "Step 1 — Create a House",
      title: "Family page → Houses → “+ Add house”.",
      body:
        "Name it “Mum's”, “Dad's”, “Grandparents”, “Cabin”, whatever feels right. You can rename or delete later. Suggested names appear if you get stuck.",
    },
    {
      eyebrow: "Step 2 — Place members in Houses",
      title: "Edit a member → pick their House.",
      body:
        "Kids can be placed freely. Adults need to enter their own personal PIN to confirm a move — each grown-up stays in control of which house they live in.",
    },
    {
      eyebrow: "Step 3 — Lock a House (optional)",
      title: "Tap the padlock next to a House.",
      body:
        "Set a 4-6 digit House PIN. Now that House's members, check-ins, events, and messages are hidden on any device that hasn't unlocked it. Unlock once per device — a handy way for Mum's iPad to stay Mum's iPad.",
    },
    {
      eyebrow: "That's Houses",
      title: "Re-open this guide from Settings any time.",
      body:
        "Settings → “Show Houses guide” brings it back. Any questions about the privacy model live at /faq under “How private is Houses, really?”.",
    },
  ],
};
